import React, { useState, useEffect, useRef } from 'react';
import {
    BarChart2,
    Activity,
    CheckCircle,
    XCircle,
    TrendingUp,
    Terminal,
    Clock,
    HardDrive,
    Layers,
    Ban,
    Zap,
    RefreshCw,
    Maximize2,
    X,
    ArrowUpRight,
    ArrowDownRight,
    AlertCircle,
    Cpu
} from 'lucide-react';
import '../sections/Sections.css'; // Re-use sections CSS
import DLQManager from './DLQManager';


function formatDuration(seconds) {
    if (!seconds || seconds < 1) return seconds + 's';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

const maskTableName = (name) => {
    if (!name || typeof name !== 'string') return name;
    if (name.includes('.')) {
        const parts = name.split('.');
        return `${parts[0]}.${maskTableName(parts[1])}`;
    }
    if (name.length <= 2) return '*';
    return `${name[0]}${'*'.repeat(Math.min(5, name.length - 2))}${name[name.length - 1]}`;
};

const renderDebeziumEvent = (payload) => {
    if (!payload || !payload.source) return null;

    const { op, before, after, source } = payload;
    const opLabel = op === 'c' ? 'CREATE' : op === 'u' ? 'UPDATE' : op === 'd' ? 'DELETE' : op;
    const opClass = op === 'c' ? 'op-create' : op === 'u' ? 'op-update' : op === 'd' ? 'op-delete' : '';

    return (
        <div className="debezium-event">
            <div className="event-header">
                <span className={`event-op ${opClass}`}>{opLabel}</span>
                <span className="event-table">{maskTableName(source.table)}</span>
                <span className="event-lsn">LSN: {source.lsn}</span>
            </div>
            <div className="event-diff">
                {before && (
                    <div className="diff-section before">
                        <div className="diff-label">PREVIOUS</div>
                        <pre>{JSON.stringify(before, null, 2)}</pre>
                    </div>
                )}
                {after && (
                    <div className="diff-section after">
                        <div className="diff-label">{before ? 'NEW' : 'DATA'}</div>
                        <pre>{JSON.stringify(after, null, 2)}</pre>
                    </div>
                )}
            </div>
        </div>
    );
};

const DMSMonitor = ({
    jobs,
    throughputData,
    selectedMonitorJobId,
    setSelectedMonitorJobId,
    logs,
    serverIP,
    jobMetrics,
    fetchJobs
}) => {
    const [stats, setStats] = useState({
        totalJobs: 0,
        runningJobs: 0,
        completedJobs: 0,
        failedJobs: 0,
        cancelledJobs: 0,
        successRate: 0,
        totalVolume: 0
    });

    const [selectedJob, setSelectedJob] = useState(null);
    const [now, setNow] = useState(new Date());
    const [expandedSection, setExpandedSection] = useState(null); // 'logs' or 'throughput'

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Update selectedJob object when ID changes
    useEffect(() => {
        if (selectedMonitorJobId) {
            const job = jobs.find(j => j.id.toString() === selectedMonitorJobId.toString());
            setSelectedJob(job);
        } else {
            setSelectedJob(null);
        }
    }, [selectedMonitorJobId, jobs]);

    // Calculate stats
    useEffect(() => {
        const calculateStats = () => {
            const total = jobs.length;
            const running = jobs.filter(job => job.status === 'running').length;
            const completed = jobs.filter(job => job.status === 'completed').length;
            const failed = jobs.filter(job => job.status === 'failed').length;
            const cancelled = jobs.filter(job => job.status === 'cancelled' || job.status === 'stopped').length;
            const successRate = total > 0 ? Math.round((completed / (completed + failed + cancelled)) * 100) : 0;
            const totalVolume = jobs.reduce((sum, job) => sum + (parseInt(job.total_bytes) || 0), 0);

            setStats({
                totalJobs: total,
                runningJobs: running,
                completedJobs: completed,
                failedJobs: failed,
                cancelledJobs: cancelled,
                successRate,
                totalVolume
            });

            // Auto-select first running job if none selected
            if (running > 0 && !selectedMonitorJobId) {
                const activeJob = jobs.find(j => j.status === 'running');
                if (activeJob) setSelectedMonitorJobId(activeJob.id);
            }
        };

        calculateStats();
    }, [jobs, selectedMonitorJobId, setSelectedMonitorJobId]);

    const renderTelemetryChart = (seriesConfigs, width = 800, height = 240, type = 'line', description = '') => {
        const hasData = seriesConfigs.some(c => c.data && c.data.length > 1);
        if (!hasData) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '180px', opacity: 0.4 }}>
                    <Activity size={32} style={{ marginBottom: '0.5rem' }} />
                    <span style={{ fontSize: '0.8rem' }}>No data yet. Start a job to see live data.</span>
                </div>
            );
        }

        // ── Layout constants ────────────────────────────────────────────────────
        const padL = 56;  // left  — room for Y-axis labels
        const padR = 16;  // right
        const padT = 20;  // top   — room for value callout
        const padB = 36;  // bottom — room for X-axis labels
        const cW = width - padL - padR;
        const cH = height - padT - padB;

        // ── Value range ─────────────────────────────────────────────────────────
        const allValues = seriesConfigs.flatMap(c => (c.data || []).map(d => d.value));
        const rawMax = Math.max(...allValues, 0);
        const maxVal = rawMax === 0 ? 1 : rawMax * 1.15;

        // ── Helpers ─────────────────────────────────────────────────────────────
        const px = (i, len) => padL + (i / Math.max(len - 1, 1)) * cW;
        const py = v => padT + cH - (Math.max(v, 0) / maxVal) * cH;

        const fmtTime = ts => {
            try {
                const d = new Date(ts);
                return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            } catch { return ''; }
        };

        const fmtVal = v => {
            if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
            if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
            return v % 1 === 0 ? String(v) : v.toFixed(2);
        };

        // ── Y-axis grid lines and labels ─────────────────────────────────────────
        const yGridCount = 4;
        const yGridLines = Array.from({ length: yGridCount + 1 }, (_, i) => {
            const fraction = i / yGridCount;
            const value = maxVal * (1 - fraction);
            const y = padT + fraction * cH;
            return { y, value };
        });

        // ── X-axis ticks: pick up to 5 evenly-spaced timestamps ─────────────────
        const firstSeries = seriesConfigs.find(c => c.data && c.data.length > 1);
        const allTimestamps = firstSeries?.data || [];
        const xTickCount = Math.min(5, allTimestamps.length);
        const xTicks = xTickCount < 2 ? [] : Array.from({ length: xTickCount }, (_, i) => {
            const idx = Math.round(i * (allTimestamps.length - 1) / (xTickCount - 1));
            return { idx, label: fmtTime(allTimestamps[idx]?.timestamp) };
        });

        // ── Render each series ───────────────────────────────────────────────────
        const renderSeries = (data, color, strokeWidth, seriesIdx) => {
            if (!data || data.length < 2) return null;
            const pts = data.map((d, i) => [px(i, data.length), py(d.value)]);

            // Smooth cubic bezier path
            const pathD = pts.reduce((acc, [x, y], i) => {
                if (i === 0) return `M ${x} ${y}`;
                const [px0, py0] = pts[i - 1];
                const cpx = (px0 + x) / 2;
                return `${acc} C ${cpx} ${py0} ${cpx} ${y} ${x} ${y}`;
            }, '');

            // Area gradient fill path
            const areaD = `${pathD} L ${pts[pts.length - 1][0]} ${padT + cH} L ${pts[0][0]} ${padT + cH} Z`;

            const gradId = `grad-${seriesIdx}-${Math.abs(color.hashCode?.() || seriesIdx)}`;
            const [lastX, lastY] = pts[pts.length - 1];
            const lastVal = data[data.length - 1]?.value ?? 0;
            const peakVal = Math.max(...data.map(d => d.value));
            const peakIdx = data.findIndex(d => d.value === peakVal);
            const [peakX, peakY] = pts[peakIdx] || [lastX, lastY];

            return (
                <g key={`series-${seriesIdx}`}>
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                        </linearGradient>
                    </defs>
                    {/* Gradient area fill */}
                    {type === 'area' && (
                        <path d={areaD} fill={`url(#${gradId})`} />
                    )}
                    {/* Smooth line */}
                    <path
                        d={pathD}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth || 2.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* Peak dot + label (only if different from last) */}
                    {peakIdx !== data.length - 1 && peakVal > 0 && (
                        <g>
                            <circle cx={peakX} cy={peakY} r={3.5} fill={color} opacity={0.6} />
                            <text x={peakX} y={peakY - 7} fill={color} fontSize="9" textAnchor="middle" opacity={0.8}>
                                peak: {fmtVal(peakVal)}
                            </text>
                        </g>
                    )}
                    {/* Latest value dot + callout */}
                    {lastVal >= 0 && (
                        <g>
                            <circle cx={lastX} cy={lastY} r={5} fill={color} opacity={0.25} />
                            <circle cx={lastX} cy={lastY} r={3} fill={color} />
                            <rect
                                x={lastX - 28} y={lastY - 24}
                                width={56} height={16}
                                rx={4} fill={color} opacity={0.85}
                            />
                            <text x={lastX} y={lastY - 13} fill="#fff" fontSize="9.5" textAnchor="middle" fontWeight="600">
                                {fmtVal(lastVal)}
                            </text>
                        </g>
                    )}
                </g>
            );
        };

        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', userSelect: 'none' }}>
                {description && (
                    <p style={{ fontSize: '0.72rem', color: 'var(--obs-text-muted)', margin: '0 0 0.5rem 0', lineHeight: 1.4 }}>{description}</p>
                )}
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio="none"
                    style={{ flex: 1, width: '100%', minHeight: 0, overflow: 'visible' }}
                >
                    {/* ── Horizontal grid + Y-axis labels ─────────────────────── */}
                    {yGridLines.map(({ y, value }, i) => (
                        <g key={`yg-${i}`}>
                            <line
                                x1={padL} y1={y} x2={padL + cW} y2={y}
                                stroke={i === yGridCount ? 'rgba(128,128,128,0.4)' : 'rgba(128,128,128,0.15)'}
                                strokeWidth={i === yGridCount ? 1.5 : 1}
                                strokeDasharray={i === yGridCount ? undefined : '4 4'}
                            />
                            <text
                                x={padL - 6} y={y + 4}
                                fill="currentColor"
                                fontSize="9.5"
                                textAnchor="end"
                                fontFamily="monospace"
                                opacity="0.6"
                            >
                                {fmtVal(value)}
                            </text>
                        </g>
                    ))}

                    {/* ── Vertical border on the left ──────────────────────────── */}
                    <line x1={padL} y1={padT} x2={padL} y2={padT + cH} stroke="rgba(128,128,128,0.4)" strokeWidth="1" />

                    {/* ── Series ───────────────────────────────────────────────── */}
                    {seriesConfigs.map((cfg, i) => renderSeries(cfg.data, cfg.color, cfg.strokeWidth, i))}

                    {/* ── X-axis ticks + labels ────────────────────────────────── */}
                    {xTicks.map(({ idx, label }) => {
                        const x = px(idx, allTimestamps.length);
                        return (
                            <g key={`xt-${idx}`}>
                                <line x1={x} y1={padT + cH} x2={x} y2={padT + cH + 4} stroke="rgba(128,128,128,0.4)" strokeWidth="1" />
                                <text x={x} y={padT + cH + 14} fill="currentColor" opacity="0.6" fontSize="9" textAnchor="middle" fontFamily="monospace">{label}</text>
                            </g>
                        );
                    })}

                    {/* X-axis label */}
                    <text
                        x={padL + cW / 2} y={height - 2}
                        fill="currentColor"
                        opacity="0.35"
                        fontSize="9"
                        textAnchor="middle"
                        letterSpacing="1"
                        fontFamily="monospace"
                    >
                        TIME (HH:MM)
                    </text>
                </svg>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {seriesConfigs.map(cfg => {
                        const vals = (cfg.data || []).map(d => d.value);
                        const cur = vals[vals.length - 1] ?? 0;
                        const peak = vals.length ? Math.max(...vals) : 0;
                        return (
                            <div key={cfg.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.72rem', opacity: 0.8 }}>
                                <div style={{ width: '24px', height: '3px', borderRadius: '2px', background: cfg.color }} />
                                <span style={{ fontWeight: 600 }}>{cfg.label}</span>
                                <span>cur: <strong style={{ color: cfg.color }}>{fmtVal(cur)}</strong></span>
                                <span>peak: <strong>{fmtVal(peak)}</strong></span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderUnifiedChart = () => {
        const data = (jobMetrics?.throughputSeries || throughputData).map(d => ({
            timestamp: d.timestamp,
            value: parseFloat(d.volume || d.currentSpeedMBs) || 0
        }));
        return renderTelemetryChart([
            { label: 'Throughput (MB/s)', data, color: 'var(--primary)', strokeWidth: 3 }
        ], 500, 180, 'area', 'Live data transfer rate from source to target database (megabytes per second).');
    };


    const renderMetricCard = (label, value, unit, trend, status = 'info', trendType = 'up') => (
        <div className={`metric-card status-${status}`}>
            <div className="metric-label">{label}</div>
            <div className="metric-value-row">
                <div className="metric-value">{value}</div>
                <div className="metric-unit">{unit}</div>
            </div>
            {trend && (
                <div className={`metric-trend trend-${trendType}`}>
                    {trendType === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {trend}
                </div>
            )}
        </div>
    );

    const renderPanelCard = (title, icon, children, span = 6) => (
        <div className={`panel-card panel-span-${span}`}>
            <div className="panel-header">
                <div className="panel-title">
                    {icon}
                    {title}
                </div>
                <div className="panel-actions">
                    <button className="action-icon-btn small"><RefreshCw size={12} /></button>
                </div>
            </div>
            <div className="panel-content">
                {children}
            </div>
        </div>
    );

    const renderEnterpriseDashboard = () => {
        if (!selectedJob) return null;

        // Use real metrics data from the API, fall back gracefully
        const m = jobMetrics;
        const isHistorical = selectedJob.status !== 'running';
        const lagSeries = (m?.lagSeries || []).map(d => ({ timestamp: d.timestamp, value: parseFloat(d.lag_ms) / 1000 || 0 }));
        const throughputSeries = (m?.throughputSeries || throughputData).map(d => ({ timestamp: d.timestamp, value: parseFloat(d.volume || d.currentSpeedMBs) || 0 }));
        const cdcSeries = m?.cdcSeries || [];
        const cdcInserts = cdcSeries.map(d => ({ timestamp: d.timestamp, value: d.inserts || 0 }));
        const cdcUpdates = cdcSeries.map(d => ({ timestamp: d.timestamp, value: d.updates || 0 }));
        const cdcDeletes = cdcSeries.map(d => ({ timestamp: d.timestamp, value: d.deletes || 0 }));
        const errorFeed = m?.errorFeed || [];
        const syncedTables = (m?.syncedTables || selectedJob.synced_tables || []);

        // Real KPI values
        const currentLagSec = m?.currentLagSec ?? ((selectedJob.current_lag_ms || 0) / 1000).toFixed(2);
        const currentSpeedMBs = m?.currentSpeedMBs ?? (parseFloat(throughputData[throughputData.length - 1]?.volume) || 0).toFixed(1);
        const errorRatePct = m?.errorRatePct ?? '0.00';
        const cdcTotal = (m?.cdcSummary?.totalInserts || 0) + (m?.cdcSummary?.totalUpdates || 0) + (m?.cdcSummary?.totalDeletes || 0);

        // Status colour
        const statusColor = {
            running: 'var(--obs-success)',
            completed: 'var(--obs-primary)',
            failed: 'var(--obs-error)',
            validation_failed: '#f97316', // Bright orange for validation mismatch
            cancelled: 'var(--obs-warning)',
            stopped: 'var(--obs-text-muted)'
        }[selectedJob.status] || 'var(--obs-text-muted)';

        return (
            <div className="enterprise-dashboard">
                {/* Job identity banner */}
                <div className="enterprise-job-banner">
                    <div className="enterprise-job-identity">
                        <span className="enterprise-job-name">{selectedJob.name || `Job #${selectedJob.id}`}</span>
                        <span className="enterprise-job-badge" style={{ background: statusColor }}>
                            {selectedJob.status?.toUpperCase()}
                        </span>
                        {isHistorical && m?.durationLabel && (
                            <span className="enterprise-job-duration">
                                <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                Duration: {m.durationLabel}
                            </span>
                        )}
                        {isHistorical && (
                            <span className="enterprise-job-hint">
                                Showing historical data from {new Date(m?.startedAt || selectedJob.created_at).toLocaleString()}
                                {m?.endedAt ? ` → ${new Date(m.endedAt).toLocaleString()}` : ''}
                            </span>
                        )}
                    </div>
                </div>

                {/* 1. Summary Metrics Header */}
                <div className="metrics-header">
                    {renderMetricCard("Total Tasks", stats.totalJobs, "jobs", `${stats.completedJobs} completed`, "info", "up")}
                    {renderMetricCard(
                        isHistorical ? "Final Status" : "Running Tasks",
                        isHistorical ? selectedJob.status : stats.runningJobs,
                        isHistorical ? '' : "active",
                        isHistorical ? (m?.durationLabel ? `Ran for ${m.durationLabel}` : '') : (stats.runningJobs > 0 ? "Replicating" : "Idle"),
                        isHistorical ? (selectedJob.status === 'completed' ? "success" : selectedJob.status === 'failed' ? "error" : "info") : (stats.runningJobs > 0 ? "success" : "info")
                    )}
                    {renderMetricCard(
                        "Replication Lag",
                        currentLagSec,
                        "sec",
                        isHistorical ? `At job end` : (parseFloat(currentLagSec) < 1 ? "Healthy" : "High — check source"),
                        parseFloat(currentLagSec) > 2 ? "error" : "success",
                        "down"
                    )}
                    {renderMetricCard("Throughput", currentSpeedMBs, "MB/s", `${(m?.totalMB || 0)} MB total`, "success", "up")}
                    {renderMetricCard(
                        "Error Rate",
                        errorRatePct,
                        "%",
                        errorFeed.length > 0 ? `${errorFeed.length} errors logged` : "No errors logged",
                        errorFeed.length > 0 ? "error" : "success"
                    )}
                </div>

                {/* 2. Main Telemetry Grid */}
                <div className="dashboard-grid">
                    {/* Replication Lag History */}
                    {renderPanelCard(
                        "Replication Lag",
                        <Activity size={16} />,
                        renderTelemetryChart([
                            { label: 'Lag (sec)', data: lagSeries, color: 'var(--obs-warning)', strokeWidth: 3 }
                        ], 840, 260, 'area',
                            'How many seconds behind the target database is compared to the source. A spike indicates the replication engine is struggling to keep up — investigate source load or network issues.'),
                        8
                    )}

                    {/* Engine Resource Saturation — derived from job speed_bps */}
                    {renderPanelCard("Engine Resource Saturation", <Cpu size={16} />, (
                        <div className="resource-grid">
                            <div className="resource-mini-chart">
                                <div className="metric-label">Throughput</div>
                                <div className="metric-value">{currentSpeedMBs} MB/s</div>
                                <div className="progress-bar-wrapper">
                                    <div className="progress-fill" style={{ width: `${Math.min(parseFloat(currentSpeedMBs) * 5, 100)}%` }}></div>
                                </div>
                            </div>
                            <div className="resource-mini-chart">
                                <div className="metric-label">Lag</div>
                                <div className="metric-value">{currentLagSec}s</div>
                                <div className="progress-bar-wrapper">
                                    <div className="progress-fill" style={{ width: `${Math.min(parseFloat(currentLagSec) * 20, 100)}%`, background: parseFloat(currentLagSec) > 2 ? 'var(--obs-error)' : 'var(--obs-success)' }}></div>
                                </div>
                            </div>
                            <div className="resource-mini-chart">
                                <div className="metric-label">Total Volume</div>
                                <div className="metric-value">{m?.totalMB || 0} MB</div>
                                <div className="progress-bar-wrapper">
                                    <div className="progress-fill" style={{ width: '100%', background: 'var(--obs-primary)' }}></div>
                                </div>
                            </div>
                            <div className="resource-mini-chart">
                                <div className="metric-label">CDC Events</div>
                                <div className="metric-value">{cdcTotal.toLocaleString()}</div>
                                <div className="progress-bar-wrapper">
                                    <div className="progress-fill" style={{ width: `${Math.min(cdcTotal / 10, 100)}%`, background: 'var(--obs-info)' }}></div>
                                </div>
                            </div>
                        </div>
                    ), 4)}

                    {/* CDC Event Activity — real Inserts / Updates / Deletes from logs */}
                    {renderPanelCard(
                        "CDC Event Activity",
                        <Zap size={16} />,
                        renderTelemetryChart([
                            { label: `Inserts (${m?.cdcSummary?.totalInserts || 0} total)`, data: cdcInserts, color: 'var(--obs-success)', strokeWidth: 2 },
                            { label: `Updates (${m?.cdcSummary?.totalUpdates || 0} total)`, data: cdcUpdates, color: 'var(--obs-primary)', strokeWidth: 2 },
                            { label: `Deletes (${m?.cdcSummary?.totalDeletes || 0} total)`, data: cdcDeletes, color: 'var(--obs-error)', strokeWidth: 2 }
                        ], 840, 260, 'area',
                            'Captured change events per minute from the source (via Debezium/WAL). Inserts = new rows; Updates = modified rows; Deletes = removed rows. Use this to spot write-heavy workloads.'),
                        8
                    )}

                    {/* Throughput History */}
                    {renderPanelCard(
                        "Data Transfer Rate (MB/s)",
                        <BarChart2 size={16} />,
                        renderTelemetryChart([
                            { label: 'MB/s', data: throughputSeries, color: 'var(--obs-info)', strokeWidth: 3 }
                        ], 400, 260, 'area',
                            'Bytes transferred per second from source to target. Dips may indicate network throttling or source load.'),
                        4
                    )}

                    {/* Database Migration Progress Monitor — Aggregated by DB */}
                    renderPanelCard("Database Migration Progress Monitor", <Layers size={16} />, (
                        <div>
                            <p style={{ fontSize: '0.75rem', color: 'var(--obs-text-muted)', margin: '0 0 0.75rem 0' }}>
                                Overall migration progress aggregated by database. COMPLETED = all tables replicated. SYNCING = actively capturing changes.
                            </p>
                            <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                <table className="table-monitor">
                                    <thead style={{ position: 'sticky', top: 0, background: 'var(--obs-card-bg)', zIndex: 10 }}>
                                        <tr>
                                            <th>Database Name</th>
                                            <th>Total Rows Synced</th>
                                            <th>Overall Progress</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Aggregate by Database Name (prefix before the dot)
                                            const dbGroups = {};
                                            syncedTables.forEach(t => {
                                                const fullName = t.table_name || t.name || 'Unknown.Unknown';
                                                const dbName = fullName.includes('.') ? fullName.split('.')[0] : 'default';
                                                
                                                if (!dbGroups[dbName]) {
                                                    dbGroups[dbName] = {
                                                        name: dbName,
                                                        rowCount: 0,
                                                        totalRows: 0,
                                                        status: 'completed',
                                                        tables: 0
                                                    };
                                                }
                                                
                                                const status = (t.status || 'pending').toLowerCase();
                                                dbGroups[dbName].rowCount += (parseInt(t.row_count) || 0);
                                                dbGroups[dbName].totalRows += (parseInt(t.total_rows) || 0);
                                                dbGroups[dbName].tables += 1;
                                                
                                                // If any table is syncing/pending, the whole DB is syncing/pending
                                                if (status !== 'completed') {
                                                    dbGroups[dbName].status = status;
                                                }
                                            });

                                            return Object.values(dbGroups).map((db, i) => {
                                                const pct = db.totalRows > 0 
                                                    ? Math.min(100, Math.round((db.rowCount / db.totalRows) * 100))
                                                    : db.status === 'completed' ? 100 : 50;

                                                const isLive = db.status === 'syncing' || db.status === 'cdc_syncing';
                                                const statusColor = db.status === 'completed' ? 'var(--obs-success)' : 'var(--obs-primary)';

                                                return (
                                                    <tr key={i}>
                                                        <td style={{ fontWeight: 600, color: 'var(--obs-primary)' }}>{db.name}</td>
                                                        <td>{db.rowCount.toLocaleString()}</td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <div className="progress-bar-wrapper" style={{ flex: 1 }}>
                                                                    <div 
                                                                        className={`progress-fill ${db.status === 'completed' ? 'complete' : ''}`}
                                                                        style={{ 
                                                                            width: `${pct}%`,
                                                                            animation: isLive ? 'shimmer 1.8s infinite' : undefined
                                                                        }}
                                                                    />
                                                                </div>
                                                                <span style={{ fontSize: '0.75rem', minWidth: '35px' }}>{pct}%</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span style={{ fontSize: '0.7rem', fontWeight: 800, color: statusColor }}>
                                                                {db.status.toUpperCase().replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                                {syncedTables.length === 0 && (
                                    <div style={{ textAlign: 'center', opacity: 0.5, padding: '1rem' }}>No migration data available.</div>
                                )}
                            </div>
                        </div>
                    ), 12)}

                    {/* Error Feed — real error-level logs */}
                    {errorFeed.length > 0 && renderPanelCard("Anomaly & Error Feed", <AlertCircle size={16} />, (
                        <div className="error-feed">
                            <p style={{ fontSize: '0.75rem', color: 'var(--obs-text-muted)', margin: '0 0 0.75rem 0' }}>
                                Error-level events captured from migration logs. Investigate each entry to identify source connectivity issues, schema mismatches, or replication aborts.
                            </p>
                            {errorFeed.map((e, i) => (
                                <div key={i} className="error-item">
                                    <div className="error-meta">
                                        <span style={{ textTransform: 'uppercase', fontWeight: 700, color: 'var(--obs-error)' }}>{e.level}</span>
                                        <span>{new Date(e.timestamp).toLocaleTimeString()}</span>
                                    </div>
                                    <div className="error-msg">{e.message}</div>
                                </div>
                            ))}
                        </div>
                    ), 12)}

                    {/* Phase 4: Direct Dead Letter Queue Visualization */}
                    <DLQManager jobId={selectedJob.id} />
                </div>
            </div>
        );
    };

    const logEndRef = useRef(null);
    const logContainerRef = useRef(null);
    const [autoScroll, setAutoScroll] = useState(false);

    useEffect(() => {
        if (selectedMonitorJobId) {
            const job = jobs.find(j => j.id.toString() === selectedMonitorJobId.toString());
            setAutoScroll(job?.status === 'running');
        }
    }, [selectedMonitorJobId, jobs]);

    const handleScroll = () => {
        if (!logContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        setAutoScroll(isAtBottom);
    };

    useEffect(() => {
        if (autoScroll && logContainerRef.current) {
            logContainerRef.current.scrollTo({
                top: logContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [logs, autoScroll]);

    return (
        <div className="dms-page-container monitor-view">
            <div className="monitor-bg-glow glow-top-right"></div>
            <div className="monitor-bg-glow glow-bottom-left"></div>

            <div className="dms-page-header">
                <div className="header-title-stack">
                    <h2>System Monitor</h2>
                    <p className="header-subtitle">Real-time analytics, performance metrics, and migration job health monitoring.</p>
                </div>
                <button type="button" className="btn-secondary" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fetchJobs) fetchJobs();
                }}>
                    <RefreshCw size={16} />
                    Refresh
                </button>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <BarChart2 size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.totalJobs}</div>
                        <div className="stat-label">Total Jobs</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon running">
                        <Zap size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.runningJobs}</div>
                        <div className="stat-label">Running</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon completed">
                        <CheckCircle size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.completedJobs}</div>
                        <div className="stat-label">Completed</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon failed">
                        <XCircle size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.failedJobs}</div>
                        <div className="stat-label">Failed</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon cancelled">
                        <Ban size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.cancelledJobs}</div>
                        <div className="stat-label">Cancelled</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon success">
                        <TrendingUp size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.successRate}%</div>
                        <div className="stat-label">Success Rate</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon data">
                        <HardDrive size={32} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">
                            {stats.totalVolume > 1024 * 1024 * 1024
                                ? (stats.totalVolume / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
                                : (stats.totalVolume / (1024 * 1024)).toFixed(1) + ' MB'}
                        </div>
                        <div className="stat-label">Total Data</div>
                    </div>
                </div>
            </div>

            <div className="monitor-details-grid">
                {/* Real-time Logs section */}
                <div className="monitor-card logs-section">
                    <div className="monitor-card-header">
                        <div className="header-title">
                            <Terminal size={20} className="header-icon" />
                            <div className="title-group">
                                <h3>Historical & Live Logs</h3>
                                <div className="subtitle">Activity stream for current or past migrations</div>
                            </div>
                        </div>
                        <div className="header-actions">
                            <select
                                value={selectedMonitorJobId || ''}
                                onChange={(e) => setSelectedMonitorJobId(e.target.value)}
                                className="job-selector"
                            >
                                <option value="">Select Migration Job</option>
                                {jobs.map(job => (
                                    <option key={job.id} value={job.id}>
                                        {job.name} ({job.status})
                                    </option>
                                ))}
                            </select>
                            <button
                                className="action-icon-btn"
                                onClick={() => setExpandedSection('logs')}
                                title="Expand Logs"
                            >
                                <Maximize2 size={16} />
                            </button>
                        </div>
                    </div>
                    {selectedJob && (
                        <>
                            <div className="job-summary-banner">
                                <div className="summary-item">
                                    <div className="summary-label-group">
                                        <Activity size={12} className="summary-icon" />
                                        <span className="summary-label">Status</span>
                                    </div>
                                    <span className={`summary-val status-${selectedJob.status.toUpperCase()}`}>
                                        {selectedJob.status.toUpperCase()}
                                    </span>
                                </div>
                                <div className="summary-item">
                                    <div className="summary-label-group">
                                        <Clock size={12} className="summary-icon" />
                                        <span className="summary-label">Duration</span>
                                    </div>
                                    <span className="summary-val">
                                        {selectedJob.started_at ? (
                                            selectedJob.completed_at ? (
                                                formatDuration(Math.floor((new Date(selectedJob.completed_at) - new Date(selectedJob.started_at)) / 1000))
                                            ) : (
                                                ['running', 'cdc_syncing', 'replicating', 'pending'].includes(selectedJob.status?.toLowerCase()) ? (
                                                    formatDuration(Math.floor((now - new Date(selectedJob.started_at)) / 1000))
                                                ) : 'N/A'
                                            )
                                        ) : 'N/A'}
                                    </span>
                                </div>
                                <div className="summary-item">
                                    <div className="summary-label-group">
                                        <Terminal size={12} className="summary-icon" />
                                        <span className="summary-label">Started At</span>
                                    </div>
                                    <span className="summary-val">
                                        {selectedJob.started_at ? new Date(selectedJob.started_at).toLocaleTimeString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="summary-item">
                                    <div className="summary-label-group">
                                        <HardDrive size={12} className="summary-icon" />
                                        <span className="summary-label">Data Migrated</span>
                                    </div>
                                    <span className="summary-val highlight">
                                        {(() => {
                                            const bytes = parseInt(selectedJob.total_bytes) || 0;
                                            if (bytes === 0) return '0 Bytes';
                                            if (bytes > 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
                                            if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                                            if (bytes > 1024) return (bytes / 1024).toFixed(2) + ' KB';
                                            return bytes + ' Bytes';
                                        })()}
                                    </span>
                                </div>
                                {(selectedJob.status === 'cdc_syncing' || selectedJob.job_type === 'cdc') && (
                                    <>
                                        <div className="summary-item">
                                            <div className="summary-label-group">
                                                <Layers size={12} className="summary-icon" />
                                                <span className="summary-label">Synced Tables</span>
                                            </div>
                                            <span className="summary-val highlight" title={selectedJob.synced_tables ? selectedJob.synced_tables.map(t => typeof t === 'object' ? t.name : t).join(', ') : 'No tables'}>
                                                {selectedJob.synced_tables?.length || 0} Tables
                                            </span>
                                        </div>
                                        <div className="summary-item">
                                            <div className="summary-label-group">
                                                <Activity size={12} className="summary-icon" />
                                                <span className="summary-label">Replication Lag</span>
                                            </div>
                                            <span className={`summary-val ${selectedJob.current_lag_ms > 1000 ? 'status-FAILED' : 'status-RUNNING'}`}>
                                                {selectedJob.current_lag_ms ? `${selectedJob.current_lag_ms} ms` : '0 ms'}
                                            </span>
                                        </div>
                                        <div className="summary-item">
                                            <div className="summary-label-group">
                                                <Layers size={12} className="summary-icon" />
                                                <span className="summary-label">Event Bus</span>
                                            </div>
                                            <span className="summary-val status-RUNNING" style={{ color: '#60a5fa' }}>
                                                Kafka (Production)
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                        </>
                    )}
                    <div
                        className="log-viewer"
                        ref={logContainerRef}
                        onScroll={handleScroll}
                        style={{ height: '340px', maxHeight: '400px', overflowY: 'auto' }}
                    >
                        {logs.length === 0 ? (
                            <div className="empty-logs">
                                <Clock size={48} className="empty-icon" />
                                <h3>No Detailed Logs</h3>
                                <p>Select a migration job from the dropdown above to view its activity history.</p>
                            </div>
                        ) : (
                            logs.map((log, idx) => (
                                <div key={idx} className={`log-entry level-${(log.level || 'info').toLowerCase()}`}>
                                    <div className="log-line">
                                        <span className="log-time">{new Date(log.created_at).toLocaleTimeString()}</span>
                                        <span className={`log-badge badge-${(log.level || 'info').toLowerCase()}`}>{(log.level || 'info').toUpperCase()}</span>
                                        <span className="log-msg">
                                            {typeof log.message === 'object' ? JSON.stringify(log.message) : log.message}
                                        </span>
                                    </div>
                                    {log.details && (log.details.payload) ? (
                                        <div className="log-debezium">
                                            {renderDebeziumEvent(log.details.payload)}
                                        </div>
                                    ) : log.details && (Object.keys(log.details).length > 0) && (
                                        <div className="log-details" style={{
                                            marginTop: '0.5rem',
                                            marginLeft: '2rem',
                                            padding: '0.5rem',
                                            background: 'rgba(0, 0, 0, 0.2)',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace',
                                            overflowX: 'auto',
                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(log.details, null, 2)}</pre>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                        <div ref={logEndRef} />
                    </div>
                </div>

                {/* Charts section */}
                <div className="monitor-card charts-section">
                    <div className="monitor-card-header">
                        <div className="header-title">
                            <Activity size={20} className="header-icon" />
                            <div className="title-group">
                                <h3>{selectedJob ? 'Live Migration Speed' : 'System-wide Data Speed'}</h3>
                                <div className="subtitle">Real-time throughput analysis</div>
                            </div>
                        </div>
                        <div className="header-actions">
                            <button
                                className="action-icon-btn"
                                onClick={() => setExpandedSection('throughput')}
                                title="Open Full Enterprise Dashboard"
                            >
                                <Maximize2 size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="charts-content">
                        <div className="chart-wrapper">
                            {renderUnifiedChart()}
                        </div>
                        <div className="mini-stats-row premium" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                            <div className="mini-stat performance-card premium">
                                <div className="mini-label-group">
                                    <span className="mini-label" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>PEAK SPEED</span>
                                </div>
                                <div className="stat-main-content">
                                    <span className="mini-val dashboard-font highlight" style={{ fontSize: '1.6rem' }}>
                                        {(() => {
                                            const series = (jobMetrics?.throughputSeries || []).map(d => parseFloat(d.volume || 0));
                                            const peakFromSeries = series.length ? Math.max(...series) : 0;
                                            const peakFromJob = parseInt(selectedJob?.speed_bps || 0) / (1024 * 1024);
                                            return Math.max(peakFromSeries, peakFromJob).toFixed(2);
                                        })()}
                                    </span>
                                    <span className="unit" style={{ fontSize: '0.8rem' }}> MB/s</span>
                                </div>
                            </div>
                            <div className="mini-stat speed-card premium">
                                <div className="mini-label-group">
                                    <span className="mini-label" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>TOTAL TRANSFERRED</span>
                                </div>
                                <div className="stat-main-content">
                                    <span className="mini-val highlight dashboard-font" style={{ fontSize: '1.6rem' }}>
                                        {(() => {
                                            const bytes = parseInt(selectedJob?.total_bytes || 0);
                                            const mb = bytes / (1024 * 1024);
                                            return mb >= 1024 ? (mb / 1024).toFixed(2) : mb.toFixed(1);
                                        })()}
                                    </span>
                                    <span className="unit" style={{ fontSize: '0.8rem' }}>
                                        {parseInt(selectedJob?.total_bytes || 0) >= 1024 * 1024 * 1024 ? ' GB' : ' MB'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Expanded Overlay Modal */}
            {expandedSection && (
                <div className="monitor-expanded-overlay">
                    <div className="overlay-content">
                        <div className="overlay-header">
                            <div className="header-title">
                                {expandedSection === 'logs' ? <Terminal size={22} color="#6366f1" /> : <Activity size={22} color="#6366f1" />}
                                <h2>{expandedSection === 'logs' ? 'Migration Logs - Full View' : 'Throughput Analysis - Full View'}</h2>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    className="close-overlay-btn"
                                    onClick={() => setExpandedSection(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#64748b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: 500
                                    }}
                                >
                                    <X size={18} /> Close
                                </button>
                            </div>
                        </div>
                        <div className="overlay-body">
                            {expandedSection === 'logs' ? (
                                <div className="expanded-logs-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <div className="log-viewer expanded">
                                        {logs.length === 0 ? (
                                            <div className="empty-logs">
                                                <Clock size={48} className="empty-icon" />
                                                <h3>No Detailed Logs</h3>
                                                <p>Monitoring for incoming activity...</p>
                                            </div>
                                        ) : (
                                            logs.map((log, idx) => (
                                                <div key={idx} className={`log-entry level-${(log.level || 'info').toLowerCase()}`}>
                                                    <div className="log-line">
                                                        <span className="log-time">{new Date(log.created_at).toLocaleTimeString()}</span>
                                                        <span className={`log-badge badge-${(log.level || 'info').toLowerCase()}`}>{(log.level || 'info').toUpperCase()}</span>
                                                        <span className="log-msg">
                                                            {typeof log.message === 'object' ? JSON.stringify(log.message) : log.message}
                                                        </span>
                                                    </div>
                                                    {log.details && (log.details.payload) ? (
                                                        <div className="log-debezium">
                                                            {renderDebeziumEvent(log.details.payload)}
                                                        </div>
                                                    ) : log.details && (Object.keys(log.details).length > 0) && (
                                                        <div className="log-details" style={{
                                                            marginTop: '0.5rem',
                                                            marginLeft: '2rem',
                                                            padding: '0.5rem',
                                                            background: 'rgba(0, 0, 0, 0.2)',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            fontFamily: 'monospace',
                                                            overflowX: 'auto',
                                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                                        }}>
                                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(log.details, null, 2)}</pre>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                        <div ref={logEndRef} />
                                    </div>
                                </div>
                            ) : (
                                renderEnterpriseDashboard()
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DMSMonitor;
